import { ContextHelpButton } from "@/components/registrant/help/context-help-button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { Omt } from "@/hera-api/client/types.gen";
import type { QuestionResponseSchema } from "@/hera-api/client/types.gen";
import {
  getRegistrationTaggedQuestion,
  omtPartyChanged,
} from "@/lib/service-transfer-wizard";

interface TransferOutstandingResponsibilityProps {
  jurisdictionOmt: Omt | null | undefined;
  questions: QuestionResponseSchema[] | undefined;
  originalOmtAnswer: unknown;
  currentAnswers: Record<string, unknown> | null | undefined;
  value: boolean | null;
  onChange: (value: boolean) => void;
}

/**
 * Shown during registration-as-transfer when OMT party changed and jurisdiction
 * is UPDATE (ask whether new registrant takes prior unpaid responsibility).
 */
export function TransferOutstandingResponsibility({
  jurisdictionOmt,
  questions,
  originalOmtAnswer,
  currentAnswers,
  value,
  onChange,
}: TransferOutstandingResponsibilityProps) {
  const omtQuestion = getRegistrationTaggedQuestion(questions, "OMT");
  if (!omtQuestion || !currentAnswers) return null;

  const currentOmt = currentAnswers[omtQuestion.id];
  const changed = omtPartyChanged(originalOmtAnswer, currentOmt);
  if (!changed) return null;
  if (jurisdictionOmt !== "update") return null;

  return (
    <div className="rounded-md border p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium text-sm">Outstanding payments</div>
        <ContextHelpButton
          articleId="service-transfer"
          iconOnly
          variant="ghost"
        />
      </div>
      <p className="text-sm text-muted-foreground">
        Will you take responsibility for all outstanding unpaid periods on the
        existing registration? Prior unpaid periods stay with the previous
        registrant if you choose no; the current period always moves to you.
      </p>
      <RadioGroup
        value={value === null ? undefined : value ? "yes" : "no"}
        onValueChange={(v) => onChange(v === "yes")}
        className="gap-3"
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="yes" id="xfer-pass-yes" />
          <Label htmlFor="xfer-pass-yes">
            Yes — take responsibility for all outstanding payments
          </Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="no" id="xfer-pass-no" />
          <Label htmlFor="xfer-pass-no">
            No — prior unpaid stay with the previous registrant
          </Label>
        </div>
      </RadioGroup>
    </div>
  );
}
